import type { Entity } from '../components'
import type { GameWorld } from '../world'
import { attachEyes } from './eyes'
import { depthFactor } from '../depth'
import { inWater } from '../dungeon/depth-field'
import { BIOMES, currentBiomeAt, maxPredatorRadiusMult } from '../biomes'
import { pickPredatorSpecies, pickFriendSpecies, morphologyFromSpecies, isApexSpecies, type SpeciesId } from '../species'
import { BOSSES } from '../bosses'
import { FEAT2 } from '../creature-recipe'
import type { EventBus } from '../events'
import { T } from '../tunables'

// Threat scales with WHERE the player is (depth) and how long this life has
// lasted — deliberately not with player size. Size-scaled counts inverted the
// power curve (growing made the game strictly harder), which made staying
// small the dominant strategy. VS-study fix, 2026-07 (see amoeba-vs-study.md).
function adaptivePredatorTarget(player: Entity, elapsed: number): number {
  const depthMult = 1 + T.predator.depthCountScale * depthFactor(player)
  const lifeRamp = Math.min(T.predator.lifeRampMax, elapsed / T.predator.lifeRampSec)
  return Math.min(T.predator.maxCountAtDepth, Math.round(T.predator.baseCount * depthMult + lifeRamp))
}

// Blend a biome predator tint toward the apex crimson (monorail-228). Keeps
// the biome hue family visible; apexTintBlend controls how far it darkens.
function apexPredatorTint(base: [number, number, number]): [number, number, number] {
  const a = T.predator.apexTint
  const t = T.predator.apexTintBlend
  return [
    base[0] + (a[0] - base[0]) * t,
    base[1] + (a[1] - base[1]) * t,
    base[2] + (a[2] - base[2]) * t,
  ]
}

function findSpawnPosition(
  world: GameWorld,
  player: Entity,
  ringMin: number,
  ringMax: number,
  selfRadius: number,
  hubExclusion = false,
): { x: number; y: number } | null {
  for (let attempt = 0; attempt < T.predator.maxPlacementAttempts; attempt++) {
    const angle = Math.random() * Math.PI * 2
    const r = ringMin + Math.random() * (ringMax - ringMin)
    const x = player.position.x + Math.cos(angle) * r
    const y = player.position.y + Math.sin(angle) * r
    if (hubExclusion && x * x + y * y < T.biomes.hubRadius * T.biomes.hubRadius) continue
    // Walled world: bodies must materialize in water with their whole
    // footprint clear of the banks.
    if (!inWater(x, y, selfRadius)) continue
    if (!overlapsExisting(world, x, y, selfRadius)) {
      return { x, y }
    }
  }
  return null
}

function overlapsExisting(world: GameWorld, x: number, y: number, selfRadius: number): boolean {
  for (const other of world.with('position', 'renderable')) {
    if (other.consumable && !other.predator) continue // microbes are fine to overlap
    if (other.chainSegment) continue // dormant boss segments don't block predator placement
    if (other.dnaOrb) continue
    const minDist = other.renderable.radius + selfRadius + T.predator.spawnMargin
    const dx = other.position.x - x
    const dy = other.position.y - y
    if (dx * dx + dy * dy < minDist * minDist) return true
  }
  return false
}

function despawnFar(world: GameWorld, player: Entity): void {
  const limit2 = T.predator.despawnDistance * T.predator.despawnDistance
  const toRemove: Entity[] = []
  for (const e of world.with('position', 'predator')) {
    const dx = e.position.x - player.position.x
    const dy = e.position.y - player.position.y
    if (dx * dx + dy * dy > limit2) toRemove.push(e)
  }
  for (const e of world.with('position', 'recruitable')) {
    const dx = e.position.x - player.position.x
    const dy = e.position.y - player.position.y
    if (dx * dx + dy * dy > limit2) toRemove.push(e)
  }
  for (const e of toRemove) {
    if (world.id(e) !== undefined) world.remove(e)
  }
}

// Build + add one predator at a position. Extracted from the spawn loop so a
// dev cheat (cheat.pred) can stage a specific species without waiting on the
// biome roll — same construction the live spawner uses, so what you review is
// what ships. speciesOverride forces a species; otherwise the biome rolls one.
export function spawnPredatorAt(
  world: GameWorld,
  pos: { x: number; y: number },
  speciesOverride?: SpeciesId,
): Entity {
  const baseRadius = 0.11
  const variant = currentBiomeAt(pos.x, pos.y)
  const mods = BIOMES[variant].predatorMods()
  const predRadius = baseRadius * mods.radiusMult
  const hp = Math.round(1 * mods.hpMult)
  const damage = 1 * mods.damageMult
  const predSpecies = speciesOverride ?? pickPredatorSpecies(variant)
  // Apex threat (monorail-228): keeps hunting even when the player outgrows
  // it, and paints a scarier deep-crimson tell so a max-size hit never reads
  // as unfair. Blend the biome tint toward apexTint rather than replacing it
  // so the biome family still shows through.
  const apex = isApexSpecies(predSpecies)
  const tint = apex ? apexPredatorTint(BIOMES[variant].predatorTint()) : BIOMES[variant].predatorTint()
  const predator: Entity = {
    position: { x: pos.x, y: pos.y },
    velocity: { x: 0, y: 0 },
    wobble: { phase: Math.random() * Math.PI * 2, freq: 1.0, amp: 0.04 },
    renderable: { shape: 'predator', radius: predRadius, tint, variant },
    morphology: morphologyFromSpecies(predSpecies),
    wander: { impulseTimer: Math.random() * 2, noiseSeed: Math.random() * 100 },
    predator: {
      detectionRadius: 1.5, speed: 0.35, damage, separation: 0.3,
      state: 'chase', stateTimer: 0, windupCooldown: 0, lungeDir: { x: 0, y: 0 },
      windupDuration: T.predator.windupDuration * mods.windupMult,
      apex,
    },
    health: { current: hp, max: hp },
  }
  // Apex predators carry a second-word feature flag so the shader can paint the
  // self-lit danger rim + pulsing nucleus (the tint alone goes muddy in shadow
  // and reads too close to a normal predator). Set here, not on the recipe, so
  // only the live apex instance gets it.
  if (apex && predator.morphology) {
    predator.morphology.featureFlags2 = (predator.morphology.featureFlags2 ?? 0) | FEAT2.APEX
  }
  // Predators are eyeless (visual direction round 3): their life shows in
  // tentacle undulation, not a face — eating one shouldn't feel like
  // eating a someone, and the ornament IS the menace.
  world.add(predator)
  return predator
}

export function spawnPredatorsSystem(world: GameWorld, player: Entity, events: EventBus, elapsed: number): void {
  despawnFar(world, player)

  // Regular predators — target scales with depth + time alive this life
  const target = adaptivePredatorTarget(player, elapsed)
  const predatorCount = [...world.with('predator')].filter(e => !e.chainSegment).length
  const predatorDeficit = target - predatorCount
  for (let i = 0; i < predatorDeficit; i++) {
    const baseRadius = 0.11
    // Clearance uses the biggest cross-biome radius since the landing biome
    // isn't known until a position is picked.
    const pos = findSpawnPosition(world, player, T.predator.spawnRingMin, T.predator.spawnRingMax, baseRadius * maxPredatorRadiusMult(), true)
    if (!pos) break
    spawnPredatorAt(world, pos)
  }

  // Recruitable friends
  const recruitableCount = [...world.with('recruitable')].length
  const recruitableDeficit = T.predator.targetRecruitable - recruitableCount
  for (let i = 0; i < recruitableDeficit; i++) {
    const friendRadius = 0.08
    const pos = findSpawnPosition(world, player, T.predator.spawnRingMin, T.predator.spawnRingMax, friendRadius)
    if (!pos) break
    const friendSpecies = pickFriendSpecies()
    // Two clearly different friendly-greens so the ally types read apart at a
    // glance — the cilia-ring vs flagella-whip ornament alone is too subtle at
    // this size. Both stay green (the "friendly" signal): ciliate = cool mint,
    // flagellate = warm lime.
    const friendTint: [number, number, number] =
      friendSpecies === 'ciliateFriend' ? [0.34, 1.0, 0.62] : [0.60, 1.0, 0.34]
    const friend: Entity = {
      position: pos,
      velocity: { x: 0, y: 0 },
      wobble: { phase: Math.random() * Math.PI * 2, freq: 1.5 + Math.random(), amp: 0.12 },
      renderable: { shape: 'friend', radius: friendRadius, tint: friendTint },
      morphology: morphologyFromSpecies(friendSpecies),
      wander: { impulseTimer: Math.random() * 2, noiseSeed: Math.random() * 100 },
      consumable: true,
      recruitable: true,
    }
    // Oversized friendly eyes (sockets ~38% of body) for chibi appeal on small creatures.
    attachEyes(friend, [
      { offset: { x: -0.34, y: 0.22 }, radius: 0.38 },
      { offset: { x:  0.34, y: 0.22 }, radius: 0.38 },
    ])
    world.add(friend)
  }

  // Chain boss — at most one per session, placed dormant when player enters deep biome.
  // Wake check (DNA gate) lives in chain-boss-ai.ts, not here.
  const spec = BOSSES.chain
  const bossExists = [...world.with('boss')].length > 0
  if (!bossExists && depthFactor(player) > spec.depthGate) {
    const anchorPos = findSpawnPosition(
      world, player,
      T.boss.chain.spawnRingMin, T.boss.chain.spawnRingMax,
      spec.linkDistance + spec.segmentRadius,
      true,
    )
    if (anchorPos) {
      // Build coil snapshot: segments arranged in a circle around the anchor.
      // Head (index 0) is at the 0° position; tail wraps around.
      const coilRadius = spec.linkDistance
      const snapshotPositions = Array.from({ length: spec.segmentCount }, (_, i) => {
        const angle = (i / spec.segmentCount) * Math.PI * 2
        return { x: anchorPos.x + Math.cos(angle) * coilRadius, y: anchorPos.y + Math.sin(angle) * coilRadius }
      })

      const totalHp = spec.segmentCount * spec.segmentHp
      const parentBoss: Entity = {
        position: { x: snapshotPositions[0].x, y: snapshotPositions[0].y },
        health: { current: totalHp, max: totalHp },
        boss: {
          id: spec.id,
          archetype: spec.archetype,
          state: 'dormant',
          stateTimer: 0,
          phase: 1,
          awakeRange: 3.0,
          recurlGrace: 5.0,
          outOfRangeTimer: 0,
        },
        chain: {
          segments: [],
          initialSnapshot: snapshotPositions.map(p => ({ x: p.x, y: p.y })),
          whipDir: { x: 0, y: 0 },
          coilCenter: { x: 0, y: 0 },
          coilAngleAtWake: 0,
        },
      }
      world.add(parentBoss)

      for (let i = 0; i < spec.segmentCount; i++) {
        const hueOffset = spec.segmentCount > 1 ? i / (spec.segmentCount - 1) : 0
        const segment: Entity = {
          position: { x: snapshotPositions[i].x, y: snapshotPositions[i].y },
          velocity: { x: 0, y: 0 },
          wobble: { phase: Math.random() * Math.PI * 2, freq: 0.6, amp: 0.04 },
          renderable: {
            shape: 'chainSegment',
            radius: spec.segmentRadius,
            tint: [0.55 + hueOffset * 0.45, 0.75 + hueOffset * 0.2, 1.0],
          },
          health: { current: spec.segmentHp, max: spec.segmentHp },
          chainSegment: {
            parent: parentBoss,
            index: i,
            hueOffset,
            popTimer: 0,
          },
        }
        world.add(segment)
        parentBoss.chain!.segments.push(segment)
      }

      events.emit({ type: 'boss.spawned', boss: parentBoss, id: spec.id })
    }
  }
}
